/**
 * Calendar-aware countdown maths.
 *
 * Months and weeks are *calendar* quantities, not fixed multiples of 24 hours,
 * so "5 months" stays truthful across months of different lengths and across a
 * daylight-saving change in the visitor's own zone. Everything below the day
 * level is plain millisecond arithmetic.
 *
 * These are pure functions of (target, now) so they can be unit tested without
 * mocking a clock.
 */

export interface CountdownParts {
  /**
   * Whole days remaining in total — the headline figure.
   * Counts every remaining day, unlike `days`, which is only the remainder
   * left over after `months` and `weeks` have been taken out.
   */
  totalDays: number
  months: number
  weeks: number
  days: number
  hours: number
  minutes: number
  seconds: number
  /** Total milliseconds remaining; negative once the target has passed. */
  remainingMs: number
  isPast: boolean
}

const MS_PER_SECOND = 1000
const MS_PER_MINUTE = 60 * MS_PER_SECOND
const MS_PER_HOUR = 60 * MS_PER_MINUTE
const MS_PER_DAY = 24 * MS_PER_HOUR

export const ZERO_COUNTDOWN: CountdownParts = {
  totalDays: 0,
  months: 0,
  weeks: 0,
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
  remainingMs: 0,
  isPast: true,
}

/**
 * Adds whole calendar months, clamping the day of month so that
 * 31 Jan + 1 month is 28/29 Feb rather than rolling into March.
 */
export function addMonths(date: Date, months: number): Date {
  const result = new Date(date.getTime())
  const dayOfMonth = result.getDate()

  result.setDate(1)
  result.setMonth(result.getMonth() + months)

  const daysInTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate()
  result.setDate(Math.min(dayOfMonth, daysInTargetMonth))

  return result
}

/** The largest number of whole calendar months that fits between two instants. */
export function wholeMonthsBetween(fromMs: number, toMs: number): number {
  if (toMs <= fromMs) return 0

  const from = new Date(fromMs)
  const to = new Date(toMs)

  let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth())

  // The estimate above ignores the time of day, so nudge it into place.
  while (months > 0 && addMonths(from, months).getTime() > toMs) months -= 1
  while (addMonths(from, months + 1).getTime() <= toMs) months += 1

  return months
}

export function getCountdown(targetMs: number, nowMs: number): CountdownParts {
  const remainingMs = targetMs - nowMs
  if (remainingMs <= 0) {
    return { ...ZERO_COUNTDOWN, remainingMs }
  }

  const months = wholeMonthsBetween(nowMs, targetMs)
  const afterMonthsMs = targetMs - addMonths(new Date(nowMs), months).getTime()

  const daysAfterMonths = Math.floor(afterMonthsMs / MS_PER_DAY)
  const withinDayMs = afterMonthsMs % MS_PER_DAY

  return {
    totalDays: Math.floor(remainingMs / MS_PER_DAY),
    months,
    weeks: Math.floor(daysAfterMonths / 7),
    days: daysAfterMonths % 7,
    hours: Math.floor(withinDayMs / MS_PER_HOUR),
    minutes: Math.floor((withinDayMs % MS_PER_HOUR) / MS_PER_MINUTE),
    seconds: Math.floor((withinDayMs % MS_PER_MINUTE) / MS_PER_SECOND),
    remainingMs,
    isPast: false,
  }
}

/** `305` → `"5m 05s"`. */
export function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`
}

export function padTwo(value: number): string {
  return Math.max(0, value).toString().padStart(2, '0')
}
